import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { landingTab, type Filters, type FilterKey, type Preset, type Search, type Tab } from "@po/shared";
import { api, reload, type Freshness, type Me } from "../lib/api";
import { usePageSync } from "../lib/page-sync";
import { readPrefs, writePrefs } from "../lib/prefs";
import { dashboardRoute } from "../router";
import { MobileBar, Sidebar } from "../components/Sidebar";
import { Banners, SessionWarning, StagingRibbon } from "../components/Banners";
import { PageHeader } from "../components/PageHeader";
import { KpiStrip } from "../components/KpiStrip";
import { Drawer, type DrawerRef } from "../components/Drawer";
import { OverviewTab } from "../tabs/OverviewTab";
import { RequisitionsTab } from "../tabs/RequisitionsTab";
import { PosTab } from "../tabs/PosTab";
import { RfqsTab } from "../tabs/RfqsTab";
import { SpendTab } from "../tabs/SpendTab";
import { BudgetsTab } from "../tabs/BudgetsTab";
import { AddressesTab, AddressInfoTab } from "../tabs/AddressesTab";
import { CertificateBrowserTab, CertificateListTab } from "../tabs/CertificatesTab";
import { ItemGroupsTab, ItemsTab, ItemTreeTab, MeasuringUnitsTab } from "../tabs/ItemInfoTab";
import { ApprovalLimitsTab } from "../tabs/ApprovalLimitsTab";
import { AccountInfoTab } from "../tabs/AccountInfoTab";
import { CompaniesTab, CurrenciesTab, DepartmentsTab } from "../tabs/CompanyCurrencyTab";
import { UnitUserInfoTab } from "../tabs/UnitUserInfoTab";
import { BudgetTiles } from "../components/budget";

type Ctx = {
  search: Search;
  filters: Filters;
  preset: Preset;
  tab: Tab;
  me: Me;
  /** The freshness poll's last answer; budget surfaces read "Budgets as of" from it. */
  freshness?: Freshness;
  setSearch: (patch: Partial<Search>) => void;
  /** Chart click: toggle one value of a filter; a second click clears it (SPEC §6.3). */
  toggle: (key: FilterKey, value: string | number) => void;
  open: (r: DrawerRef) => void;
  /** The drawer on top, if any: the Budgets tab highlights the line it shows. */
  drawer?: DrawerRef;
};
const DashboardContext = createContext<Ctx | null>(null);
export const useDashboard = () => useContext(DashboardContext)!;

// No "q": the search box filters only the active tab's list, not the tiles and charts (SPEC §6.3).
const FILTER_KEYS: FilterKey[] = [
  "vessel", "type", "dept", "stage", "poStage", "flags", "urgent", "supplier", "region", "showDoNotUse", "approver", "project", "from", "to", "purchaseType", "account", "age", "cycle",
];

/** The default filters of a preset: the vessel preset selects the user's vessels (SPEC §6.2). */
export function presetDefaults(preset: Preset, me: Me): Partial<Search> {
  if (preset !== "vessel") return {};
  const own = readPrefs().vessels;
  const vessels = own === undefined || own === null ? me.superintendentVessels : own;
  return vessels.length ? { vessel: vessels } : {};
}

export function Dashboard() {
  const search = dashboardRoute.useSearch();
  const navigate = useNavigate({ from: "/" });
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/me"), staleTime: Infinity });
  const [stack, setStack] = useState<DrawerRef[]>([]);

  // First visit / bare URL: pick the remembered preset, else Vessel technical for a superintendent.
  useEffect(() => {
    if (!me.data || search.preset) return;
    const prefs = readPrefs();
    const preset: Preset = prefs.preset ?? (me.data.superintendentVessels.length ? "vessel" : "procurement");
    // An explicit tab in a shared URL wins over the remembered one.
    const tab = search.tab ?? prefs.tab ?? landingTab(preset, me.data.canSeeBudgets);
    void navigate({ search: { ...presetDefaults(preset, me.data), ...search, preset, tab }, replace: true });
  }, [me.data, search, navigate]);

  // Pages left open poll freshness every 60 s; when "as of" moves, visible queries reload quietly.
  const fresh = useQuery({
    queryKey: ["freshness"],
    queryFn: () => api<Freshness>("/freshness", { background: true }),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
  const lastAsOf = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const asOf = fresh.data?.asOf;
    if (asOf === undefined) return;
    if (lastAsOf.current !== undefined && asOf && asOf !== lastAsOf.current) {
      reload.background = true;
      void qc
        .invalidateQueries({ predicate: (q) => q.queryKey[0] !== "freshness" && q.queryKey[0] !== "me" })
        .finally(() => (reload.background = false));
    }
    lastAsOf.current = asOf;
  }, [fresh.data?.asOf, qc]);

  const ctx = useMemo<Ctx | null>(() => {
    if (!me.data || !search.preset) return null;
    const filters: Filters = {};
    for (const k of FILTER_KEYS) if (search[k] !== undefined) (filters as Record<string, unknown>)[k] = search[k];
    const setSearch = (patch: Partial<Search>) => {
      if (patch.preset || patch.tab) writePrefs({ preset: patch.preset ?? search.preset, tab: patch.tab ?? search.tab });
      if ("vessel" in patch && (search.preset === "vessel" || patch.preset === "vessel")) writePrefs({ vessels: patch.vessel ?? [] });
      void navigate({ search: (prev: Search) => ({ ...prev, ...patch }), replace: false });
    };
    return {
      search,
      filters,
      preset: search.preset,
      tab: search.tab ?? landingTab(search.preset, me.data.canSeeBudgets),
      me: me.data,
      freshness: fresh.data,
      setSearch,
      toggle: (key, value) => {
        const cur = ((search as Record<string, unknown>)[key] as (string | number)[] | undefined) ?? [];
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
        setSearch({ [key]: next.length ? next : undefined } as Partial<Search>);
      },
      open: (r) => setStack((s) => [...s, r]),
      drawer: stack[stack.length - 1],
    };
  }, [me.data, search, navigate, fresh.data, stack]);
  // The open page syncs its own part from Sertica when that is older than 5 min (SPEC §6.3).
  usePageSync(ctx?.tab);

  if (me.isError) return <div className="p-6 text-muted">Could not load your session.</div>;
  if (!ctx) return <div className="p-6 text-muted">Loading…</div>;

  return (
    <DashboardContext.Provider value={ctx}>
      <div className="min-h-screen lg:flex">
        <Sidebar freshness={fresh.data} />
        <div className="flex-1 min-w-0 pb-10">
          {me.data!.env === "staging" && <StagingRibbon />}
          <MobileBar freshness={fresh.data} />
          <Banners freshness={fresh.data} />
          <SessionWarning me={me.data!} />
          <main className="space-y-3">
            <PageHeader />
            <div className="px-4 md:px-5 space-y-4">
              {ctx.tab === "budgets" && ctx.me.canSeeBudgets && fresh.data?.budgets.asOf && <BudgetTiles />}
              {ctx.tab === "overview" && <KpiStrip />}
              {ctx.tab === "overview" && <OverviewTab />}
              {ctx.tab === "requisitions" && <RequisitionsTab />}
              {ctx.tab === "rfqs" && <RfqsTab />}
              {ctx.tab === "pos" && <PosTab />}
              {ctx.tab === "spend" && <SpendTab />}
              {ctx.tab === "budgets" && <BudgetsTab />}
              {ctx.tab === "addresses" && <AddressesTab />}
              <AddressInfoTab tab={ctx.tab} />
              {ctx.tab === "items" && <ItemsTab />}
              {ctx.tab === "item-groups" && <ItemGroupsTab />}
              {ctx.tab === "item-tree" && <ItemTreeTab />}
              {ctx.tab === "measuring-units" && <MeasuringUnitsTab />}
              {ctx.tab === "companies" && <CompaniesTab />}
              {ctx.tab === "approval-limits" && <ApprovalLimitsTab />}
              {ctx.tab === "currencies" && <CurrenciesTab />}
              {ctx.tab === "departments" && <DepartmentsTab />}
              <AccountInfoTab tab={ctx.tab} />
              <UnitUserInfoTab tab={ctx.tab} rights={ctx.me.userInfoRights} />
              <CertificateListTab tab={ctx.tab} />
              {ctx.tab === "certificate-browser" && <CertificateBrowserTab />}
            </div>
          </main>
        </div>
        {stack.length > 0 && (
          <Drawer
            current={stack[stack.length - 1]!}
            behind={stack[stack.length - 2]}
            canGoBack={stack.length > 1}
            onBack={() => setStack((s) => s.slice(0, -1))}
            onClose={() => setStack([])}
            onOpen={(r) => setStack((s) => [...s, r])}
            onReplace={(r) => setStack((s) => [...s.slice(0, -1), r])}
            freshness={fresh.data}
          />
        )}
      </div>
    </DashboardContext.Provider>
  );
}
